
import React, { useMemo } from 'react';
import type { VoteDistribution as VoteDistributionType } from '../governanceService';

interface VoteDistributionProps {
    distribution: VoteDistributionType;
    userChoice?: string;
}

export const VoteDistribution: React.FC<VoteDistributionProps> = ({ distribution, userChoice }) => {
    // CRITICAL: Use useMemo to prevent flicker during polling
    const processedData = useMemo(() => {
        if (!distribution || distribution.total_weight === 0) {
            return { rows: [], hasData: false };
        }

        // Calculate percentages by WEIGHT (not votes)
        const rows = Object.entries(distribution.by_choice).map(([choice, data]) => ({
            choice,
            votes: data.votes,
            weight: data.weight,
            percentage: (data.weight / distribution.total_weight) * 100,
            voters: data.voters
        }));

        // Sort by weight descending (highest first)
        rows.sort((a, b) => b.weight - a.weight);

        return { rows, hasData: true };
    }, [distribution]);

    if (!processedData.hasData) {
        return (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                No votes yet
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div>
                <h4 className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-3">
                    Vote Distribution
                </h4>

                <div className="space-y-3">
                    {processedData.rows.map((row) => (
                        <div key={row.choice} className="space-y-1">
                            <div className="flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2">
                                    {userChoice === row.choice && (
                                        <span className="text-green-600 dark:text-green-400">✓</span>
                                    )}
                                    <span className="font-mono font-medium text-slate-900 dark:text-slate-100">
                                        {row.choice}
                                    </span>
                                </div>
                                <div className="text-xs text-slate-500 dark:text-slate-400">
                                    {row.votes} vote{row.votes !== 1 ? 's' : ''} ({row.weight.toFixed(1)} weight)
                                </div>
                            </div>

                            {/* Progress bar */}
                            <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
                                <div
                                    className="bg-blue-600 dark:bg-blue-500 h-full transition-all duration-300"
                                    style={{ width: `${row.percentage}%` }}
                                />
                            </div>

                            <div className="text-xs text-slate-500 dark:text-slate-400">
                                {row.percentage.toFixed(1)}%
                            </div>

                            {/* Voters (collapsed by default, can expand if needed) */}
                            {row.voters.length > 0 && (
                                <details className="mt-2">
                                    <summary className="text-xs text-blue-600 dark:text-blue-400 cursor-pointer hover:underline">
                                        View {row.voters.length} voter{row.voters.length !== 1 ? 's' : ''}
                                    </summary>
                                    <div className="mt-2 space-y-2 pl-4 border-l-2 border-slate-200 dark:border-slate-700">
                                        {row.voters.map((voter, idx) => (
                                            <div key={idx} className="text-xs">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-[10px] font-bold">
                                                        {voter.voter_type}
                                                    </span>
                                                </div>
                                                {/* CRITICAL: ONLY render justification_preview, NEVER justification */}
                                                <div className="text-slate-600 dark:text-slate-400 italic">
                                                    "{voter.justification_preview}"
                                                    {voter.has_full_justification && (
                                                        <span className="text-slate-400 dark:text-slate-500"> ...</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </details>
                            )}
                        </div>
                    ))}
                </div>

                <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700 text-xs text-slate-500 dark:text-slate-400">
                    Total: {distribution.total_votes} vote{distribution.total_votes !== 1 ? 's' : ''}, {distribution.total_weight.toFixed(1)} weight
                </div>
            </div>

            {/* CRITICAL: Minority votes ALWAYS visible (never hidden) */}
            {distribution.minority_votes && distribution.minority_votes.length > 0 && (
                <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                    <h4 className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-3">
                        Dissenting Opinions ({distribution.minority_votes.length})
                    </h4>

                    <div className="space-y-3">
                        {distribution.minority_votes.map((vote, idx) => (
                            <div key={idx} className="p-3 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="font-mono text-sm font-medium text-slate-900 dark:text-slate-100">
                                        {vote.choice}
                                    </span>
                                    <span className="text-xs text-slate-500 dark:text-slate-400">
                                        by {vote.voter_type}
                                    </span>
                                </div>
                                {/* CRITICAL: ONLY render justification_preview, NEVER justification */}
                                <div className="text-xs text-slate-600 dark:text-slate-400 italic">
                                    "{vote.justification_preview}"
                                    {vote.has_full_justification && (
                                        <span className="text-slate-400 dark:text-slate-500"> ...</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
